import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindowSymbol,
	CompsLifeCycleSym,
	FeatureStateSymbol,
	PageFeatureConfigSymbol,
	ReducedMotionSymbol,
} from '@wix/thunderbolt-symbols'
import { name, MotionEffectsInitCallbackSymbol, MOTION_EFFECTS_CALLBACK } from './symbols'
import { MotionEffectsManager } from './MotionEffectsManager'
import { Animations } from 'feature-animations'
import { MotionEffectsInitFactory } from './types'

const motionEffectsInitFactory: MotionEffectsInitFactory = (
	featureConfig,
	featureState,
	getMotionEffectsInitCallback,
	compsLifeCycle,
	browserWindow,
	reducedMotion,
	animationsProvider
) => {
	let unregisterFromCompLifeCycle = () => {}

	const motionEffectsManagerInitPromise: Promise<MotionEffectsManager | undefined> = new Promise((resolve) => {
		const { animationDefsByCompId, isResponsive } = featureConfig

		if (!animationsProvider) {
			resolve(undefined)
		}

		animationsProvider!.getInstance().then((animationsManager) => {
			const motionEffectsManager =
				featureState.get()?.motionEffectsManager ?? new MotionEffectsManager(animationsManager, isResponsive)

			const allCompsEffectsMap = Object.assign({}, ...Object.values(animationDefsByCompId))

			motionEffectsManager.init(allCompsEffectsMap)
			featureState.update((state) => ({ ...state, motionEffectsManager }))

			resolve(motionEffectsManager)
		})
	})

	return {
		name: 'motionEffectsInit',
		async pageWillMount() {
			const motionEffectsManager = await motionEffectsManagerInitPromise

			const initCallback = getMotionEffectsInitCallback(motionEffectsManager)

			if (!initCallback) {
				return
			}

			unregisterFromCompLifeCycle = compsLifeCycle.registerToCompLifeCycle(
				Object.keys(featureConfig.animationDefsByCompId),
				MOTION_EFFECTS_CALLBACK,
				initCallback
			)
		},
		pageWillUnmount() {
			unregisterFromCompLifeCycle()
		},
		getInstance() {
			return motionEffectsManagerInitPromise
		},
	}
}

export const MotionEffectsInit = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		MotionEffectsInitCallbackSymbol,
		CompsLifeCycleSym,
		BrowserWindowSymbol,
		ReducedMotionSymbol,
		optional(Animations),
	],
	motionEffectsInitFactory
)
